'use client';

import { ArrowRightIcon, CaretDownIcon } from '@phosphor-icons/react';
import Image from 'next/image';
import { useEffect, useState, type ReactNode } from 'react';

import { cn } from '@/shared/utils/cn';

import { NewsletterForm } from '@/features/forms/components/newsletter';

import {
  DEFAULT_GIFTCARD_URL,
  DEFAULT_HOME_URL,
  DEFAULT_LOGO_SRC,
  FOOTER_ICON_MAP,
  GIFT_CARD_ICON,
  type FooterContactGroup,
  type FooterCountryLink,
  type FooterLink,
  type FooterSection,
  type FooterSocialLink,
} from '../lib/footer.config';
import type { FooterWidgetProps } from '../types/footer';

const SECTION_LABEL_CLASS =
  'text-adipa-footer-section font-bold uppercase tracking-[0.18em] text-adipa-footer-label';

const FOOTER_LINK_CLASS =
  'inline-flex min-h-10 items-center rounded-adipa-sm px-1 text-adipa-footer-link font-normal leading-6 text-adipa-footer-text-secondary transition-colors duration-adipa-base ease-out hover:text-cyan focus-visible:text-cyan focus-visible:outline-none';

const ACTIVE_NAV_LINK_CLASS = 'text-cyan font-medium';

const GROUP_ITEMS_CLASS = 'divide-y divide-adipa-footer-surface-hover';
const NAV_LINK_CLASS = `${FOOTER_LINK_CLASS} min-h-[2rem] text-adipa-footer-nav leading-[1.5] py-2 w-full`;
const CONTACT_LINK_CLASS = [
  FOOTER_LINK_CLASS,
  'min-h-0 items-start py-0.5 text-adipa-footer-nav leading-[1.55]',
].join(' ');
const SECONDARY_TEXT_CLASS = 'text-adipa-footer-text-muted';
const COUNTRY_LINK_CLASS =
  'inline-flex min-h-11 w-full items-center gap-3 rounded-adipa-sm px-2 ' +
  'text-adipa-footer-link font-normal text-adipa-footer-text-primary ' +
  'transition-[background-color,color] duration-adipa-fast ease-out hover:bg-adipa-footer-surface-hover hover:text-cyan';
const SOCIAL_LINK_CLASS =
  'group inline-flex h-9 w-9 items-center justify-center rounded-full bg-adipa-surface text-adipa-text-primary ' +
  'shadow-adipa-card transition-[transform,background-color,color,box-shadow] duration-300 ease-in-out ' +
  'hover:-translate-y-0.5 hover:bg-adipa-primary hover:text-adipa-primary-foreground hover:shadow-adipa-console-panel ' +
  'motion-reduce:transition-none motion-reduce:hover:translate-y-0';
const FOOTER_LEGAL_CLASS =
  `flex flex-col gap-2 border-t border-adipa-footer-line pt-5 text-adipa-footer-legal ` +
  `${SECONDARY_TEXT_CLASS} sm:flex-row sm:items-center sm:justify-between`;

const getExternalRel = (target?: FooterLink['target']) =>
  target === '_blank' ? 'noreferrer nofollow' : undefined;

const renderLinkItems = (links: readonly FooterLink[], isActive?: (href: string) => boolean) => (
  <ul className={GROUP_ITEMS_CLASS}>
    {links.map((link) => (
      <li key={link.label}>
        <a
          aria-label={`Ir a ${link.label}`}
          className={cn(NAV_LINK_CLASS, isActive?.(link.href) && ACTIVE_NAV_LINK_CLASS)}
          href={link.href}
          rel={getExternalRel(link.target)}
          target={link.target}
        >
          <span>{link.label}</span>
          {link.badge ? (
            <span className="border-adipa-footer-label/35 bg-adipa-footer-accent-surface text-adipa-footer-badge text-adipa-footer-label ml-1.5 inline-flex h-5 items-center rounded-full border px-2 font-semibold uppercase tracking-[0.08em]">
              {link.badge}
            </span>
          ) : null}
        </a>
      </li>
    ))}
  </ul>
);

const renderLinkSections = (
  sections: readonly FooterSection[],
  isActive?: (href: string) => boolean,
) =>
  sections.map((section) => (
    <section className="space-y-3.5" key={section.title}>
      <h3 className={SECTION_LABEL_CLASS}>{section.title}</h3>
      {renderLinkItems(section.links, isActive)}
    </section>
  ));

const getContactIcon = (group: FooterContactGroup) =>
  group.Icon ?? FOOTER_ICON_MAP[group.icon ?? 'phone'];

const getSocialIcon = (social: FooterSocialLink) =>
  social.Icon ?? FOOTER_ICON_MAP[social.icon ?? 'facebook'];

const ContactColumn = ({ contactGroups }: { contactGroups: readonly FooterContactGroup[] }) => (
  <section aria-label="Contacto ADIPA" className="space-y-4">
    <h3 className={SECTION_LABEL_CLASS}>Contacto</h3>
    <ul className="divide-adipa-footer-line divide-y">
      {contactGroups.map((group) => {
        const Icon = getContactIcon(group);

        return (
          <li className="space-y-2.5 py-4 first:pt-0 last:pb-0" key={group.title}>
            <p className="text-adipa-footer-contact-label text-adipa-footer-text-secondary inline-flex items-center gap-2 font-medium uppercase tracking-[0.14em]">
              <Icon aria-hidden="true" size={16} weight="bold" />
              {group.title}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.label}>
                  <a
                    aria-label={`Ir a ${item.label}`}
                    className={CONTACT_LINK_CLASS}
                    href={item.href}
                    rel="noreferrer nofollow"
                    target="_blank"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </li>
        );
      })}
    </ul>
  </section>
);

const ExtrasColumn = ({
  extraLinks,
  giftcardUrl,
  hideGiftcard = false,
  isActive,
}: {
  extraLinks: readonly FooterLink[];
  giftcardUrl: string;
  hideGiftcard?: boolean;
  isActive?: (href: string) => boolean;
}) => {
  const GiftIcon = GIFT_CARD_ICON;

  return (
    <section aria-label="Extras ADIPA" className="space-y-4">
      <h3 className={SECTION_LABEL_CLASS}>Extras</h3>

      {!hideGiftcard ? (
      <a
        aria-label="Ir a Giftcard ADIPA"
        className="rounded-adipa-md border-adipa-secondary/35 bg-adipa-secondary/10 hover:border-adipa-secondary hover:bg-adipa-secondary/15 hover:shadow-adipa-console-panel group relative block overflow-hidden border p-4 transition-[transform,box-shadow,border-color,background-color] duration-300 ease-in-out hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
        href={giftcardUrl}
        rel="noreferrer nofollow"
        target="_blank"
      >
        <span
          aria-hidden="true"
          className="rounded-adipa-full bg-adipa-secondary/25 pointer-events-none absolute -right-4 -top-4 h-20 w-20 blur-2xl"
        />

        <div className="relative flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-adipa-footer-card-label text-adipa-secondary font-semibold uppercase tracking-[0.16em]">
              Giftcard
            </p>
            <p className="text-adipa-footer-card-copy text-adipa-footer-text-secondary font-normal leading-snug">
              Regala aprendizaje que transforma vidas.
            </p>
          </div>
          <span className="rounded-adipa-full border-adipa-secondary/35 bg-adipa-secondary/15 text-adipa-secondary duration-adipa-base group-hover:border-adipa-secondary group-hover:bg-adipa-secondary/25 mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center border transition-[background-color,border-color]">
            <GiftIcon aria-hidden="true" size={17} weight="duotone" />
          </span>
        </div>

        <p className="text-adipa-footer-legal text-adipa-secondary relative mt-3 inline-flex items-center gap-1.5 font-semibold">
          Ver giftcard
          <ArrowRightIcon
            aria-hidden="true"
            className="duration-adipa-base transition-transform ease-out group-hover:translate-x-1"
            size={14}
            weight="bold"
          />
        </p>
      </a>
      ) : null}

      <ul className="divide-adipa-footer-line divide-y">
        {extraLinks.map((link) => (
          <li key={link.label}>
            <a
              aria-label={`Ir a ${link.label}`}
              className={cn(
                FOOTER_LINK_CLASS,
                'text-adipa-footer-nav w-full justify-between gap-2.5 py-1',
                isActive?.(link.href) && ACTIVE_NAV_LINK_CLASS,
              )}
              href={link.href}
              rel={getExternalRel(link.target)}
              target={link.target}
            >
              <span>{link.label}</span>
              <ArrowRightIcon aria-hidden="true" className="shrink-0" size={13} weight="bold" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};

const MobileFooterSection = ({ children, title }: { children: ReactNode; title: string }) => (
  <details className="rounded-adipa-md border-adipa-footer-line-strong bg-adipa-footer-surface group border">
    <summary className="text-adipa-footer-mobile-title text-adipa-footer-text-primary focus-visible:outline-cyan flex min-h-12 cursor-pointer list-none items-center justify-between px-4 font-medium uppercase tracking-[0.12em] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
      {title}
      <CaretDownIcon
        aria-hidden="true"
        className="transition-transform duration-200 ease-out group-open:rotate-180"
        size={16}
        weight="bold"
      />
    </summary>
    <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-300 ease-out group-open:grid-rows-[1fr]">
      <div className="min-h-0 overflow-hidden">
        <div className="px-4 pb-4">{children}</div>
      </div>
    </div>
  </details>
);

const Footer = ({
  logoSrc = DEFAULT_LOGO_SRC,
  homeUrl = DEFAULT_HOME_URL,
  giftcardUrl = DEFAULT_GIFTCARD_URL,
  content,
  hideNewsletter = false,
  hideGiftcard = false,
  currentYear: currentYearProp,
  newsletter,
}: FooterWidgetProps) => {
  const [currentPath, setCurrentPath] = useState('');

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  const isActive = (href: string): boolean => {
    if (!currentPath || !href) return false;
    try {
      const url = new URL(href, window.location.origin);
      return url.pathname === currentPath;
    } catch {
      return href === currentPath;
    }
  };

  const currentYear = currentYearProp ?? new Date().getFullYear();
  const description = content?.description ?? '';
  const countryLinks: FooterCountryLink[] = content?.countryLinks ?? [];
  const programSections = content?.programSections ?? [];
  const resourceSections = content?.resourceSections ?? [];
  const contactGroups = content?.contactGroups ?? [];
  const extraLinks = content?.extraLinks ?? [];
  const socialLinks = content?.socialLinks ?? [];
  const copyrightText = content?.copyrightText ?? `© ${currentYear}`;
  const legalText = content?.legalText ?? '';

  return (
    <footer
      className="border-adipa-footer-line bg-adipa-footer-bg text-adipa-footer-text-primary border-t"
      role="contentinfo"
    >
      <div className="adipa-shell pb-6 pt-11 md:pb-8 md:pt-14">
        {!hideNewsletter ? <NewsletterForm variant="embedded" {...newsletter} /> : null}

        <div className="grid gap-y-8 pb-10 md:gap-y-10 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] lg:gap-x-16 lg:gap-y-0">
          <div className="space-y-10">
            <a
              aria-label="Ir a inicio ADIPA"
              className="inline-block"
              href={homeUrl}
              rel="noreferrer"
              target="_blank"
            >
              <Image
                alt="ADIPA"
                height={42}
                src={logoSrc}
                style={{ height: '42px', width: '132px' }}
                unoptimized
                width={132}
              />
            </a>

            <p className={`text-adipa-footer-link max-w-[33ch] leading-7 ${SECONDARY_TEXT_CLASS}`}>
              {description}
            </p>

            <section aria-label="Países donde ADIPA está presente" className="space-y-[1.125rem]">
              <h3 className={SECTION_LABEL_CLASS}>Estamos presentes en:</h3>
              <ul className="space-y-1.5">
                {countryLinks.map((country) => (
                  <li key={country.label}>
                    <a
                      aria-label={`Ir a ADIPA ${country.label}`}
                      className={COUNTRY_LINK_CLASS}
                      href={country.href}
                      rel="noreferrer"
                      target="_blank"
                    >
                      <Image
                        alt={`${country.label} flag`}
                        height={14}
                        src={country.flagSrc}
                        style={{
                          display: 'block',
                          height: '14px',
                          width: '20px',
                          objectFit: 'contain',
                        }}
                        unoptimized
                        width={20}
                      />
                      <span>{country.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>

            <section aria-label="Redes sociales ADIPA" className="space-y-[1.125rem]">
              <h3 className={SECTION_LABEL_CLASS}>Síguenos</h3>
              <div className="flex flex-wrap gap-2.5">
                {socialLinks.map((social) => {
                  const Icon = getSocialIcon(social);

                  return (
                    <a
                      aria-label={`Ir a ${social.label}`}
                      className={SOCIAL_LINK_CLASS}
                      href={social.href}
                      key={social.label}
                      rel="noreferrer nofollow"
                      target="_blank"
                    >
                      <Icon
                        aria-hidden="true"
                        className="duration-400 opacity-85 transition-[transform,opacity] ease-out group-hover:scale-105 group-hover:opacity-100"
                        size={18}
                      />
                    </a>
                  );
                })}
              </div>
            </section>
          </div>

          <div className="space-y-6">
            <div
              aria-label="Navegación del footer"
              className="hidden md:grid md:grid-cols-2 md:items-start md:gap-x-12 md:gap-y-11 xl:grid-cols-4 xl:gap-x-16"
            >
              <div className="space-y-9">{renderLinkSections(programSections, isActive)}</div>
              <div className="space-y-9">{renderLinkSections(resourceSections, isActive)}</div>
              <ContactColumn contactGroups={contactGroups} />
              <ExtrasColumn
                extraLinks={extraLinks}
                giftcardUrl={giftcardUrl}
                hideGiftcard={hideGiftcard}
                isActive={isActive}
              />
            </div>

            <div aria-label="Navegación del footer móvil" className="space-y-6 md:hidden">
              {[...programSections, ...resourceSections].map((section) => (
                <MobileFooterSection key={section.title} title={section.title}>
                  {renderLinkItems(section.links, isActive)}
                </MobileFooterSection>
              ))}

              <ContactColumn contactGroups={contactGroups} />
              <ExtrasColumn
                extraLinks={extraLinks}
                giftcardUrl={giftcardUrl}
                hideGiftcard={hideGiftcard}
                isActive={isActive}
              />
            </div>
          </div>
        </div>

        <div className={FOOTER_LEGAL_CLASS}>
          <p>{copyrightText}</p>
          <p>{legalText}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
export { Footer as FooterWidget };
